import { Injectable, inject, signal, Signal, DestroyRef } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { RestApiBase } from '../shared/configurations/rest-api-base.config';
import { UrlUtils } from '../shared/utils/url.utils';
import { DepositFileMetadata } from './deposit-file-metadata';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { ResourceTypeTemplateKeys } from '../shared/configurations/resource-type-template-keys.constant';
import { DepositFormDataService } from './deposit-form-data.service';
import { AmazonInfo } from '../shared/interfaces/amazon-info.interface';
import { JwtUtilService } from '../core/jwt-util.service';
import { TranslateService } from '@ngx-translate/core';
import { StudentDepositProfile } from '../shared/interfaces/student/student-deposit.interface';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmDialogComponent } from '../parts/dialogs/confirm-dialog/confirm-dialog.component';
import { ErrorSnackbarComponent } from '../parts/snackbars/error-snackbar/error-snackbar.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { of, Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class DepositFormService {
    private http = inject(HttpClient);
    private dataSvc = inject(DepositFormDataService);
    private jwtUtilService = inject(JwtUtilService);
    private translate = inject(TranslateService);
    private _snackBar = inject(MatSnackBar);
    private matDialog = inject(MatDialog);

    readonly fileUploadRegex = new RegExp('/esploro/(.+)/');
    destroyRef = inject(DestroyRef);

    institutionCode = UrlUtils.getParam('institution');
    amazonInfo = signal<AmazonInfo>(undefined);
    draftInProcess = signal(false);
    needToSaveDraftInOpen = signal(false);
    mmsToDeleteInDiscard;
    disableCloseDialogDuringFirstSave = signal(false);

    constructor() {
        this.updateAmazonInfo();
    }

    updateAmazonInfo() {
        this.http
            .get<AmazonInfo>(RestApiBase.AMAZOM_INFO)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data) => {
                this.amazonInfo.set(data);
                const match = this.fileUploadRegex.exec(data.uploadPath);
                if (match) {
                    this.dataSvc.updateFilePath(match[1]);
                }
            });
    }

    getAdditionalDataOnDepositor() {
        let params = new HttpParams();
        params = params
            .set('institution', this.institutionCode)
            .set('userId', this.jwtUtilService.getUserID());
        return this.http.get(RestApiBase.ADDITIONAL_DATA_ON_DEPOSITOR, {
            params: params,
        });
    }

    submitNewAsset(depositFormData) {
        let params = new HttpParams();
        params = params.set('institution', this.institutionCode);
        return this.http.post(RestApiBase.SUBMIT_NEW_ASSET, depositFormData, {
            params: params,
            responseType: 'text',
        });
    }

    submitFilesAndLinks(filesData) {
        let params = new HttpParams();
        params = params.set('institution', this.institutionCode);
        return this.http.post(RestApiBase.SUBMIT_FILES_AND_LINKS, filesData, {
            params: params,
            responseType: 'text',
        });
    }

    saveDraft(depositFormData, fromAutoSave, fromNewForm = false) {
        let params = new HttpParams();
        params = params
            .set('institution', this.institutionCode)
            .set('fromAutoSave', fromAutoSave);
        if (fromNewForm) {
            params = params.set('fromNewForm', fromNewForm);
        }
        return this.http.post(RestApiBase.SAVE_DRAFT, depositFormData, {
            params: params,
            responseType: 'text',
        });
    }

    deleteDeposit(mmsId: string) {
        const params = new HttpParams()
            .set('mms', mmsId)
            .set('institution', this.institutionCode);
        return this.http.delete(RestApiBase.DELETE_DEPOSIT, { params: params });
    }

    buildDepositData(handleFiles = true): UntypedFormGroup {
        let filesMetadata: DepositFileMetadata[] = [];
        if (handleFiles) {
            filesMetadata = this.getFilesMetadata();
        }
        const depositData = this.buildDepositDataToSubmit();
        depositData.value.filesMetadata = filesMetadata;
        depositData.value.deletedStorageFilesNames = {};
        if (handleFiles) {
            depositData.value.deletedStorageFilesNames =
                this.dataSvc.deletedStorageFilesNames;
        }
        if (!handleFiles) {
            depositData.value.filePath = '';
        }
        return depositData;
    }

    getFilesMetadata(): DepositFileMetadata[] {
        const filesMetadata: DepositFileMetadata[] = [];
        const accessRightsMap = {
            public: 'Open',
            embargoed: 'Fixed Embargo Date',
            request: 'Restricted',
            private: 'Closed',
            'Research:Open': 'Open',
            MetadataRestricted: 'Restricted',
            Embargo: 'Fixed Embargo Date',
        };
        this.dataSvc.filesForm.value.forEach((file) => {
            const fileMetadata: DepositFileMetadata = {
                ...file,
                filename: file.name,
                esploroLicenseCode: file.license,
                esploroFileType: file.contentType,
                accessRightsType: accessRightsMap[file.access] || file.access,
            };

            filesMetadata.push(fileMetadata);
        });
        return filesMetadata;
    }

    buildDepositDataToSubmit() {
        const copy = new UntypedFormGroup({});

        ResourceTypeTemplateKeys.allDepositKeys.forEach((key) => {
            const newControl = new UntypedFormControl(
                this.dataSvc.mainForm.get(key).value,
            );
            copy.addControl(key, newControl);
        });

        if (
            this.dataSvc.isPublicationJournalArticle() ||
            this.dataSvc.isPublicationBookReview()
        ) {
            ResourceTypeTemplateKeys.journalArticleKeys.forEach((key) => {
                const newControl = new UntypedFormControl(
                    this.dataSvc.mainForm.get(key).value,
                );
                copy.addControl(key, newControl);
            });
        } else if (this.dataSvc.isPublicationBookChapter()) {
            ResourceTypeTemplateKeys.bookChapterKeys.forEach((key) => {
                const newControl = new UntypedFormControl(
                    this.dataSvc.mainForm.get(key).value,
                );
                copy.addControl(key, newControl);
            });
        } else if (this.dataSvc.isPublicationBook()) {
            ResourceTypeTemplateKeys.bookKeys.forEach((key) => {
                const newControl = new UntypedFormControl(
                    this.dataSvc.mainForm.get(key).value,
                );
                copy.addControl(key, newControl);
            });
        } else if (this.dataSvc.isPublicationConferenceProceedings()) {
            ResourceTypeTemplateKeys.conferenceProceedingKeys.forEach((key) => {
                const newControl = new UntypedFormControl(
                    this.dataSvc.mainForm.get(key).value,
                );
                copy.addControl(key, newControl);
            });
        } else if (this.dataSvc.isConference()) {
            ResourceTypeTemplateKeys.conferenceKeys.forEach((key) => {
                const newControl = new UntypedFormControl(
                    this.dataSvc.mainForm.get(key).value,
                );
                copy.addControl(key, newControl);
            });
        } else if (this.dataSvc.isPresentation()) {
            ResourceTypeTemplateKeys.presentationKeys.forEach((key) => {
                const newControl = new UntypedFormControl(
                    this.dataSvc.mainForm.get(key).value,
                );
                copy.addControl(key, newControl);
            });
        } else if (this.dataSvc.isDataset()) {
            ResourceTypeTemplateKeys.datasetKeys.forEach((key) => {
                const newControl = new UntypedFormControl(
                    this.dataSvc.mainForm.get(key).value,
                );
                copy.addControl(key, newControl);
            });
        } else {
            ResourceTypeTemplateKeys.defaultKeys.forEach((key) => {
                const newControl = new UntypedFormControl(
                    this.dataSvc.mainForm.get(key).value,
                );
                copy.addControl(key, newControl);
            });
        }
        return copy;
    }

    getResearcherFormConfiguration(assetType: string) {
        let params = new HttpParams();
        params = params
            .set('institution', this.institutionCode)
            .set('assetType', assetType)
            .set('language', this.translate.currentLang);
        return this.http.get<StudentDepositProfile>(
            RestApiBase.GET_RESEARCHER_DEPOSIT_PROFILE,
            {
                params: params,
            },
        );
    }

    get saveDraft$() {
        if (this.dataSvc.mainForm.dirty) {
            const deactivateSubject = new Subject<boolean>();
            const isDraft = this.dataSvc.isDraft();

            this.matDialog
                .open(ConfirmDialogComponent, {
                    maxWidth: '500px',
                    data: {
                        title: isDraft
                            ? 'research.popup.exit.draft.title'
                            : 'research.popup.exit.save.draft',
                        message: isDraft
                            ? 'research.popup.exit.draft.message'
                            : 'research.popup.exit.newdeposit.title',
                        actions: {
                            discard: 'research.popup.exit.newdeposit.discard',
                            confirm: 'research.popup.exit.newdeposit.save',
                            cancel: 'research.popup.exit.keep.editing',
                        },
                        actionFocus: 'confirm',
                    },
                })
                .afterClosed()
                .subscribe((result) => {
                    const handleError = (e) => {
                        console.error(e);
                        deactivateSubject.next(false);
                        this._snackBar.openFromComponent(
                            ErrorSnackbarComponent,
                            {
                                horizontalPosition: 'center',
                                verticalPosition: 'top',
                            },
                        );
                    };
                    if (result === 'ok') {
                        const depositData = this.buildDepositData();
                        this.saveDraft(depositData.value, false)
                            .pipe(takeUntilDestroyed(this.destroyRef))
                            .subscribe({
                                next: () => {
                                    deactivateSubject.next(true);
                                    this.resetForm();
                                },
                                error: handleError,
                            });
                    } else if (result === 'discard') {
                        this.mmsToDeleteInDiscard = this.dataSvc.mmsId.value;

                        if (this.mmsToDeleteInDiscard && !isDraft) {
                            this.deleteDeposit(
                                this.mmsToDeleteInDiscard,
                            ).subscribe({
                                next: () => {
                                    deactivateSubject.next(true);
                                    this.resetForm();
                                },
                                error: handleError,
                            });
                        } else {
                            deactivateSubject.next(true);
                            this.resetForm();
                            this.mmsToDeleteInDiscard = null;
                        }
                    } else {
                        deactivateSubject.next(false);
                    }
                });
            return deactivateSubject.asObservable();
        } else {
            this.resetForm();
            return of(true);
        }
    }

    resetForm() {
        setTimeout(() => {
            this.dataSvc.resetForm();
            this.updateAmazonInfo();
        });
    }
}
